import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { usePageMeta } from '../lib/usePageMeta';
import { usePublishedBlogPosts, type PublishedBlogPost } from '../hooks/useBlogPosts';

const PAGE_SIZE = 6;

// Note: BlogPostLegacy.tsx still resolves at /blog/how-to-publish-a-book-on-amazon-kindle-2026
// for direct URL access (the dispatcher special-cases that slug), but it's intentionally
// excluded from this listing so only Firestore-backed posts appear.

function formatDate(d: Date | null): string {
  if (!d) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function categoryEmoji(category: string): string {
  switch (category.toLowerCase()) {
    case 'publishing': return '📚';
    case 'craft': return '✍️';
    case 'market': return '📈';
    case 'marketing': return '🎯';
    case 'business': return '📊';
    default: return '📝';
  }
}

export function Blog() {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  usePageMeta({
    title: 'StoryMint Blog - Craft, Publishing, and the Business of Fiction',
    description:
      'Practical guides for indie fiction authors: Amazon KDP, cover design, pricing, AI writing workflows, and growing your readership. New posts every week.',
    canonical: 'https://storymint.io/blog',
    ogType: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'StoryMint Blog',
      description:
        'Craft, publishing, and the business of writing fiction. Guides and analysis from the StoryMint team.',
      url: 'https://storymint.io/blog',
      publisher: {
        '@type': 'Organization',
        name: 'StoryMint',
        logo: { '@type': 'ImageObject', url: 'https://storymint.io/logo.png' },
      },
    },
  });

  const { data: livePosts, isLoading } = usePublishedBlogPosts();
  const allPosts: PublishedBlogPost[] = livePosts ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allPosts;
    return allPosts.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.author.name.toLowerCase().includes(q),
    );
  }, [allPosts, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const visible = filtered.slice(start, start + PAGE_SIZE);

  const handleSearchChange = (value: string) => {
    setQuery(value);
    setPage(1);
  };

  const goToPage = (next: number) => {
    setPage(Math.min(Math.max(1, next), totalPages));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="public" />

      {/* Hero */}
      <section className="max-w-4xl mx-auto text-center px-4 sm:px-6 py-16 sm:py-20">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-[#a78bfa] text-sm font-medium mb-6">
          ✍️ Notes from the StoryMint team
        </div>
        <h1 className="text-[clamp(2.25rem,6vw,3.75rem)] font-extrabold text-foreground mb-6 leading-[1.15] tracking-tight">
          The StoryMint <span className="text-primary">Blog</span>
        </h1>
        <p className="text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-[1.65]">
          Craft, publishing, and the business of writing fiction in 2026. New posts every week.
        </p>
      </section>

      {/* Search */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-10">
        <label htmlFor="blog-search" className="sr-only">Search blog posts</label>
        <div className="relative">
          <span aria-hidden="true" className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">🔍</span>
          <input
            id="blog-search"
            type="search"
            value={query}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by title, topic, or author..."
            className="w-full pl-11 pr-4 py-3 rounded-md bg-card border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/40 transition-colors"
          />
        </div>
        <p className="text-xs text-muted-foreground mt-3 text-center">
          {isLoading
            ? 'Loading posts…'
            : `${filtered.length} ${filtered.length === 1 ? 'post' : 'posts'}${query.trim() ? ` matching "${query.trim()}"` : ''}`}
        </p>
      </section>

      {/* Posts grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        {isLoading && allPosts.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16 border border-border rounded-xl bg-card">
            <div className="text-4xl mb-3" aria-hidden="true">🪶</div>
            <h2 className="text-lg font-semibold text-foreground mb-2">No posts found</h2>
            <p className="text-muted-foreground text-sm">
              Try a different search term, or clear the search to see every post.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {visible.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group flex flex-col rounded-xl border border-border bg-card overflow-hidden hover:border-primary/35 transition-colors"
              >
                <div
                  className="h-40 shrink-0 relative overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, #0d0d12 0%, #1a1a2e 100%)' }}
                >
                  {post.coverImage ? (
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div
                      className="absolute inset-0 flex items-center justify-center text-5xl"
                      aria-hidden="true"
                    >
                      {categoryEmoji(post.category)}
                    </div>
                  )}
                </div>
                <div className="flex flex-col flex-1 p-6">
                  <div className="text-xs font-bold text-primary tracking-[0.12em] uppercase mb-2">
                    {post.category}
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2 leading-snug group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-[1.65] mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto pt-3 border-t border-border">
                    <span>{post.author.name}</span>
                    <span>
                      {formatDate(post.publishedAt)} · {post.readingTime}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {filtered.length > PAGE_SIZE && (
          <nav aria-label="Blog pagination" className="flex items-center justify-center gap-2 mt-12">
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => goToPage(n)}
                aria-current={n === currentPage ? 'page' : undefined}
                className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                  n === currentPage
                    ? 'border-primary bg-primary/10 text-primary font-semibold'
                    : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </nav>
        )}
      </section>

      <Footer />
    </div>
  );
}
